import {
  RECEIPT_DISCOUNT_TYPES,
  RECEIPT_FLOW_STATES,
  RECEIPT_PAYMENT_MODE_ID,
} from "@elixir/types";
import {
  getDefaultReceiptPaymode,
  getDefaultReceiptService,
  getReceiptStoreDataFromAppointmentReceipt,
} from "@elixir/utils";
import { create } from "zustand";
import { ReceiptStore } from "./types";

export enum PROGRESS_STEPS {
  CREATING_RECEIPT = "creating_receipt",
  GENERATING_PDF = "generating_pdf",
  PDF_SUCCESS = "pdf_success",
  SAVING_RESPONSE = "saving_response",
  COMPLETED = "completed",
  ERROR = "error",
}

const useReceiptStore = create<ReceiptStore>((set, get) => ({
  aid: null,
  receiptId: null,
  receiptState: RECEIPT_FLOW_STATES.NONE,
  onReceiptSuccess: undefined,
  setOnReceiptSuccess: (callback) => set({ onReceiptSuccess: callback }),
  currentStep: null,
  isProcessing: false,
  setIsProcessing: (processing) => set({ isProcessing: processing }),
  receiptData: null,
  pdfConfigForGeneration: null,
  shouldSendMessage: undefined,
  patientData: undefined,
  doctorProfile: undefined,
  commEventName: undefined,
  forceServerReceiptGeneration: false,
  setforceServerReceiptGeneration: (forceServerReceiptGeneration) =>
    set({ forceServerReceiptGeneration }),

  setCurrentStep: (step) => set({ currentStep: step }),
  setReceiptPayload: (payload) => set(payload),
  typeOfReceipt: RECEIPT_FLOW_STATES.NONE,
  setTypeOfReceipt: (typeOfReceipt) => {
    set(() => ({ typeOfReceipt }));
  },
  receipt: null,
  setReceipt: (receipt) => {
    set({ receipt });
  },

  setReceiptPopupData: (receiptPopupData) => {
    set(receiptPopupData);
  },

  setCloseReceiptPopup: () => {
    set(() => ({ ...getInitState(), onReceiptSuccess: undefined }));
  },
  setReceiptState: (receiptState) => {
    set({
      receiptState,
    });
  },

  patientName: "",
  setPatientName: (patientName) => {
    set(() => ({ patientName }));
  },

  uhid: "",
  setUhid: (uhid) => {
    if (uhid) {
      set(() => ({ uhid }));
    }
  },
  receiptFileNameV2: "",
  setReceiptFileNameV2: (receiptFileNameV2) => {
    //@ts-ignore
    set({ receiptFileNameV2 });
  },

  phoneNumber: "",
  setPhoneNumber: (phoneNumber) => {
    set({ phoneNumber });
  },

  fullPhoneNumber: "",
  setFullPhoneNumber: (fullPhoneNumber) => {
    set({ fullPhoneNumber });
  },

  services: [],
  setServices: (services) => {
    if (services) {
      set(() => ({ services }));
    }
  },

  patientId: "",
  clinicId: "",

  paymodes: [getDefaultReceiptPaymode({})],
  setPaymodes: (paymodes) => {
    if (paymodes) {
      set(() => ({ paymodes }));
    }
  },

  additionalDiscountConfig: {
    discount: 0,
    discountToShowWithType: "0",
    discountType: RECEIPT_DISCOUNT_TYPES[0],
  },
  setAdditionalDiscountConfig: (additionalDiscountConfig) => {
    if (additionalDiscountConfig) {
      set(() => ({ additionalDiscountConfig }));
    }
  },

  createReceiptDisabled: false,
  setCreateReceiptDisabled: (createReceiptDisabled) => {
    set(() => ({ createReceiptDisabled }));
  },
  patientOwes: 0,
  setPatientOwes: (patientOwes) =>
    set({ patientOwes, amountSettled: patientOwes }),

  amountSettled: 0,
  setAmountSettled: (amountSettled) => set({ amountSettled }),

  closeSettleDuePopupAction: "CLOSE",
  setCloseSettleDuePopupAction: (action) =>
    set({ closeSettleDuePopupAction: action }),

  selectedAppointment: null,
  setSelectedAppointment: (appointment) => {
    set({ selectedAppointment: appointment });
  },

  remarks: "",
  setRemarks: (remarks) => set({ remarks }),

  paymentId: "",
  setPaymentId: (paymentId) => set({ paymentId }),

  openNewReceiptPopup: ({ appointment }) => {
    if (appointment) {
      const storeData = getReceiptStoreDataFromAppointmentReceipt({
        appointment,
      });

      set({
        ...getInitState(),
        ...storeData,
      });
    } else {
      set({
        receiptState: RECEIPT_FLOW_STATES.CREATE_RECEIPT,
        services: [getDefaultReceiptService({})],
        paymodes: [getDefaultReceiptPaymode({})],
      });
    }
  },

  editReceipt: ({ appointment, receiptId }) => {
    if (appointment && receiptId) {
      const storeData = getReceiptStoreDataFromAppointmentReceipt({
        appointment,
        receiptId,
      });
      set({
        ...getInitState(),
        ...storeData,
      });
    } else {
      set({ receiptState: RECEIPT_FLOW_STATES.EDIT_RECEIPT });
    }
  },

  sendPaymentLink: ({ appointment }) => {
    const storeData = getReceiptStoreDataFromAppointmentReceipt({
      appointment,
    });
    set({
      ...getInitState(),
      ...storeData,
      receiptState: RECEIPT_FLOW_STATES.SEND_PAYMENT_LINK,
      paymodes: [{ paymode: RECEIPT_PAYMENT_MODE_ID.EKA_POSTPAY, amount: 0 }],
      doctor_oid: appointment.doctor_oid,
    });
  },

  settleDues: ({ appointment }) => {
    const amountToSettle = appointment?.patient?.amount_pending || 0;
    const defaultPaymode = getDefaultReceiptPaymode({});
    const storeData = getReceiptStoreDataFromAppointmentReceipt({
      appointment,
    });
    set({
      ...getInitState(),
      ...storeData,
      receiptState: RECEIPT_FLOW_STATES.SETTLE_DUES,
      amountSettled: amountToSettle,
      selectedAppointment: appointment,
      aid: appointment.aid,
      services: [],
      paymodes: [{ ...defaultPaymode, amount: amountToSettle }],
      remarks: "",
    });
  },

  viewReceipt: ({ receipt, appointment }) => {
    set(({ selectedAppointment }) => ({
      receiptId: receipt.receipt_id,
      receiptState: RECEIPT_FLOW_STATES.VIEW_SINGLE_RECEIPT,
      receipt: receipt,
      selectedAppointment: !!appointment ? appointment : selectedAppointment,
    }));
  },

  viewAllReceipt: ({ appointment }) => {
    const receiptCount = appointment.receipts.length;
    const { patientName, uhid, phoneNumber, fullPhoneNumber } =
      getReceiptStoreDataFromAppointmentReceipt({
        appointment,
      });

    set({
      selectedAppointment: appointment,
      receiptState: RECEIPT_FLOW_STATES.VIEW_ALL_RECEIPTS,
      aid: appointment.aid,
      patientName,
      uhid,
      phoneNumber,
      fullPhoneNumber,
    });
  },

  createReceiptRaw: ({
    aid,
    patient,
    uhid,
    doctor_oid,
    clinicId,
    serviceInfo,
    createNewAppt,
  }) => {
    let fullPhoneNumber = patient.mobile || "";
    // Check for country code: priority is phone.code > ccd > default +91
    const countryCode =
      (patient as any)?.phone?.code || (patient as any)?.ccd || "+91";
    if (fullPhoneNumber && !fullPhoneNumber.startsWith("+")) {
      fullPhoneNumber = `${countryCode}${fullPhoneNumber}`;
    } else if (fullPhoneNumber && fullPhoneNumber.startsWith("+")) {
      console.log(
        "bill debug createReceiptRaw - mobile already has country code"
      );
    }

    set({
      ...getInitState(),
      receiptState: RECEIPT_FLOW_STATES.CREATE_RECEIPT,
      patientName: patient.fln,
      phoneNumber: patient.mobile?.substring(patient.mobile.length - 10) || "",
      fullPhoneNumber: fullPhoneNumber,
      uhid: uhid || "",
      patientId: patient.oid || "",
      clinicId: clinicId,
      doctor_oid: doctor_oid,
      services: serviceInfo
        ? [
          getDefaultReceiptService({
            skuItem: {
              service_id: serviceInfo.id,
              service_name: serviceInfo.name,
              price: serviceInfo.price,
            },
          }),
        ]
        : [getDefaultReceiptService({})],
      paymodes: [
        serviceInfo
          ? getDefaultReceiptPaymode({
            total: serviceInfo.price,
          })
          : getDefaultReceiptPaymode({}),
      ],
      aid,
      remarks: "",
      createNewAppt: !!createNewAppt,
    });
  },
  settleDuesRaw: ({
    aid,
    patient,
    uhid,
    clinicId,
    serviceInfo,
    doctor_oid,
  }) => {
    const { amount_pending } = patient;

    set({
      ...getInitState(),
      receiptState: RECEIPT_FLOW_STATES.SETTLE_DUES,
      patientName: patient.fln,
      phoneNumber: patient.mobile?.substring(patient.mobile.length - 10) || "",
      uhid: uhid || "",
      patientId: patient.oid || "",
      clinicId: clinicId,
      paymodes: [
        getDefaultReceiptPaymode({
          total: amount_pending,
        }),
      ],
      aid,
      remarks: "",
      patientOwes: !!amount_pending ? amount_pending : 0,
      amountSettled: !!amount_pending ? amount_pending : 0,
      doctor_oid: doctor_oid,
    });
  },
}));

function getInitState(): Partial<ReceiptStore> {
  return {
    aid: null,
    receiptId: null,
    receiptState: RECEIPT_FLOW_STATES.NONE,
    // NOTE: onReceiptSuccess is NOT reset here - it's set separately before modal opens
    // and cleared explicitly in setCloseReceiptPopup
    receipt: null,
    patientName: "",
    fullPhoneNumber: "",
    uhid: "",
    phoneNumber: "",
    services: [],
    patientId: "",
    clinicId: "",
    doctor_oid: "",
    paymodes: [getDefaultReceiptPaymode({})],
    additionalDiscountConfig: {
      discount: 0,
      discountToShowWithType: "0",
      discountType: RECEIPT_DISCOUNT_TYPES[0],
    },

    createReceiptDisabled: false,
    patientOwes: 0,

    amountSettled: 0,

    closeSettleDuePopupAction: "CLOSE",
    selectedAppointment: null,
    createNewAppt: false,
  };
}

export default useReceiptStore;
